import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'react-toastify';
import { getPublicBooking } from '../services/maps';
import { getBooking, saveBooking } from '../utils/session';
import './PaymentPage.css';

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const PaymentPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();

  const cached = getBooking();
  const initial = cached?.bookingCode === code ? cached : null;

  const [booking, setBooking] = useState(initial?.booking || null);
  const [payment, setPayment] = useState(initial?.payment || null);
  const [whatsapp, setWhatsapp] = useState(initial?.whatsapp || null);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (booking && payment) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getPublicBooking(code);
        if (cancelled) return;
        setBooking(res.booking || null);
        setPayment(res.payment || null);
        setWhatsapp(res.whatsapp || null);
        saveBooking({
          bookingCode: code,
          booking: res.booking || null,
          payment: res.payment || null,
          whatsapp: res.whatsapp || null,
        });
      } catch {
        if (!cancelled) setError('Could not load your booking. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code, booking, payment]);

  if (loading) {
    return (
      <div className="payment-page">
        <div className="payment-card"><p>Loading payment…</p></div>
      </div>
    );
  }
  if (error && !booking) {
    return (
      <div className="payment-page">
        <div className="payment-card">
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <button className="pp-done-btn" onClick={() => navigate('/')}>Back to home</button>
        </div>
      </div>
    );
  }
  if (!booking) return <Navigate to="/" replace />;

  const amount = payment?.amount ?? Number(booking.advanceAmount) ?? 0;
  const balance = booking.fare && amount
    ? Number(booking.fare) - Number(amount)
    : null;

  const copyVpa = () => {
    if (!payment?.upiVpa) return;
    navigator.clipboard?.writeText(payment.upiVpa).then(
      () => toast.success('UPI ID copied'),
      () => toast.error('Could not copy'),
    );
  };

  const handlePaid = () => {
    navigate(`/booking/${code}`);
  };

  return (
    <div className="payment-page">
      <div className="payment-card">
        <div className="pp-header">
          <div className="pp-badge">Step 3 of 3 · Pay advance</div>
          <h2>Pay {formatINR(amount)} to confirm your booking</h2>
          <p className="pp-sub">
            Booking <strong>{booking.bookingCode || code}</strong> · Scan the QR with any UPI app (GPay, PhonePe, Paytm, BHIM).
          </p>
        </div>

        {payment?.upiDeepLink && (
          <div className="pp-qr-wrap">
            <QRCodeSVG
              value={payment.upiDeepLink}
              size={220}
              level="M"
              includeMargin
            />
          </div>
        )}

        <div className="pp-amount">
          <div className="pp-amount-label">Amount to pay (advance)</div>
          <div className="pp-amount-value">{formatINR(amount)}</div>
          {balance != null && (
            <div className="pp-amount-note">Balance {formatINR(balance)} payable to driver</div>
          )}
        </div>

        {payment?.upiDeepLink && (
          <a className="pp-pay-btn" href={payment.upiDeepLink}>
            Pay {formatINR(amount)} via UPI app
          </a>
        )}

        {payment?.upiVpa && (
          <div className="pp-vpa">
            <span>UPI ID:</span>
            <code>{payment.upiVpa}</code>
            <button type="button" onClick={copyVpa}>Copy</button>
          </div>
        )}

        <div className="pp-divider" />

        <div className="pp-info">
          <h4>What happens after payment?</h4>
          <ul>
            <li>Our admin verifies your UPI payment (usually within a few minutes).</li>
            <li>You'll get a WhatsApp / SMS confirmation with your driver details.</li>
            <li>Show your booking code <strong>{booking.bookingCode || code}</strong> at pickup.</li>
          </ul>
        </div>

        {whatsapp?.admin?.url && (
          <a className="pp-wa-btn" href={whatsapp.admin.url} target="_blank" rel="noopener noreferrer">
            📲 Notify admin on WhatsApp
          </a>
        )}

        <button className="pp-done-btn" onClick={handlePaid}>
          I have paid — view booking →
        </button>
      </div>
    </div>
  );
};

export default PaymentPage;
